import { z } from 'zod'

import { type AppSupabaseClient } from '@/platform/supabase/client'
import { callRpc } from '@/platform/supabase/rpc'

/**
 * Family and parent-PIN API (FR-01, SEC-C).
 *
 * Contracts: backend-contracts v0.1 §4.1–§4.2, implemented by
 * `supabase/migrations/20261031000001_family_identity.sql`. The actor is
 * always derived server-side from the session; nothing here sends a user or
 * family ID as proof of access.
 */

export const familyContextSchema = z.object({
  family_id: z.uuid(),
  family_name: z.string(),
  timezone: z.string(),
  role: z.enum(['owner', 'parent', 'viewer']),
  pin_configured: z.boolean(),
  pin_locked_until: z.string().nullable(),
})
export type FamilyContext = z.infer<typeof familyContextSchema>

export const pinResultSchema = z.object({
  verified: z.boolean(),
  locked_until: z.string().nullable(),
  attempts_remaining: z.number().int(),
  configured: z.boolean().optional(),
})
export type PinResult = z.infer<typeof pinResultSchema>

/** PIN format accepted by `set_parent_pin`: 4–6 digits. */
export const PIN_PATTERN = /^[0-9]{4,6}$/
export const PIN_MIN_LENGTH = 4
export const PIN_MAX_LENGTH = 6

export interface FamilyApi {
  bootstrapFamily(): Promise<FamilyContext>
  setParentPin(newPin: string, currentPin?: string): Promise<PinResult>
  verifyParentPin(pin: string): Promise<PinResult>
}

export function createFamilyApi(client: AppSupabaseClient): FamilyApi {
  return {
    bootstrapFamily: () =>
      callRpc(
        client,
        'bootstrap_family',
        {},
        familyContextSchema.extend({ created: z.boolean() }),
      ),
    setParentPin: (newPin, currentPin) =>
      callRpc(
        client,
        'set_parent_pin',
        { p_new_pin: newPin, p_current_pin: currentPin ?? null },
        pinResultSchema,
      ),
    verifyParentPin: (pin) =>
      callRpc(client, 'verify_parent_pin', { p_pin: pin }, pinResultSchema),
  }
}
